import { nextTick, onMounted, onUnmounted, ref } from 'vue'

const ClassNames = {
  Full: 'view-in--full',
  In: 'view-in',
  GtHalf: 'view-in--gt-half',
  GtThird: 'view-in--gt-third',
  Out: 'view-out',
  Above: 'view-out--above',
  Below: 'view-out--below',
}

const EventTypes = {
  Enter: 'enter',
  Exit: 'exit',
  Progress: 'progress',
}

function throttle (handler, timeout = 40) {
  if (!handler || typeof handler !== 'function') {
    throw new Error('Throttle handler argument is not incorrect. Must be a function.')
  }
  let timeoutTime = 0
  return function (e) {
    if (timeoutTime) return
    timeoutTime = setTimeout(() => {
      timeoutTime = 0
      handler(e)
    }, timeout)
  }
}

function roundPercent (v) {
  return (v * 1000 | 0) / 1000
}

export function useView (element, options = {}) {
  const isInView = ref(false)
  const percentInView = ref(0)
  const percentTop = ref(0)
  const percentCenter = ref(0)
  const scrollPercent = ref(0)
  const scrollValue = ref(0)
  const rect = ref({})

  let scrollThrottledHandler
  let lastScrollValue = 0
  let isOnce = false

  const {
    handler,
    once = false,
    throttle: throttleTime = 40,
  } = options

  function getInType (el) {
    if (!el || !el.getBoundingClientRect) return [0, 0, 0, 0, {}]

    const viewportTop = window.pageYOffset
    const viewportBottom = window.pageYOffset + window.document.documentElement.clientHeight
    const viewportHeight = window.document.documentElement.clientHeight
    const elementRect = el.getBoundingClientRect()
    const elementTop = elementRect.top + viewportTop
    const elementBottom = elementTop + elementRect.height
    const topIn = elementTop > viewportTop && elementTop < viewportBottom
    const bottomIn = elementBottom > viewportTop && elementBottom < viewportBottom
    const percentInView = topIn || bottomIn
      ? ((bottomIn ? elementBottom : viewportBottom) - (topIn ? elementTop : viewportTop)) / elementRect.height
      : 0
    const centerPercent = (elementTop - viewportTop + elementRect.height / 2) / viewportHeight
    const zeroPoint = viewportTop - elementRect.height
    const topPercent = (elementTop - zeroPoint) / (viewportBottom - zeroPoint)
    const isAbove = percentInView === 0 && elementTop < viewportTop
    const isBelow = percentInView === 0 && elementTop > viewportTop

    const type = (topIn ? 1 : 0) | (bottomIn ? 2 : 0) | (isAbove ? 4 : 0) | (isBelow ? 8 : 0)

    return [
      type,
      roundPercent(percentInView),
      roundPercent(centerPercent),
      roundPercent(topPercent),
      elementRect,
    ]
  }

  function updateClasses (el, type, percentInView) {
    if (!el) return

    const classList = el.classList

    // Remove all view classes
    Object.values(ClassNames).forEach(className => {
      classList.remove(className)
    })

    // Add appropriate classes
    if (percentInView >= 0.5) {
      classList.add(ClassNames.GtHalf)
    } else if (percentInView >= 0.3) {
      classList.add(ClassNames.GtThird)
    }

    if (type === 8) {
      classList.add(ClassNames.Below, ClassNames.Out)
    } else if (type === 4) {
      classList.add(ClassNames.Above, ClassNames.Out)
    } else if (type === 3) {
      classList.add(ClassNames.Full, ClassNames.In)
    } else if (type === 1 || type === 2) {
      classList.add(ClassNames.In)
    }
  }

  function scrollHandler () {
    if (!element.value) return

    // Get actual DOM element from Vue component ref
    const el = element.value.$el || element.value
    if (!el || !el.getBoundingClientRect) return

    const viewportTop = window.pageYOffset
    const viewportBottom = window.pageYOffset + window.document.documentElement.clientHeight
    const viewportHeight = window.document.documentElement.clientHeight
    const documentHeight = window.document.documentElement.scrollHeight
    const currentScrollPercent = roundPercent(window.pageYOffset / (documentHeight - viewportHeight))

    scrollValue.value = viewportTop - lastScrollValue
    scrollPercent.value = currentScrollPercent

    const [type, currentPercentInView, currentPercentCenter, currentPercentTop, elementRect] = getInType(el)

    const wasInView = isInView.value
    const inViewChange = !wasInView && currentPercentInView > 0
    const outViewChange = wasInView && currentPercentInView === 0

    if (currentPercentInView === 0 && !wasInView) {
      lastScrollValue = viewportTop
      return
    }

    rect.value = elementRect
    percentInView.value = currentPercentInView
    percentTop.value = currentPercentTop
    percentCenter.value = currentPercentCenter
    isInView.value = currentPercentInView > 0

    updateClasses(el, type, currentPercentInView)

    let eventType = (inViewChange && EventTypes.Enter) || (outViewChange && EventTypes.Exit) || EventTypes.Progress

    if (typeof handler === 'function') {
      handler({
        type: eventType,
        percentInView: currentPercentInView,
        percentTop: currentPercentTop,
        percentCenter: currentPercentCenter,
        scrollPercent: currentScrollPercent,
        scrollValue: scrollValue.value,
        target: { element: el, rect: elementRect },
      })
    }

    if (once && inViewChange && typeof handler === 'function') {
      isOnce = true
      // Remove event listeners after first enter
      window.removeEventListener('scroll', scrollThrottledHandler)
      window.removeEventListener('resize', scrollThrottledHandler)
    }

    lastScrollValue = viewportTop
  }

  onMounted(() => {
    if (import.meta.client) {
      scrollThrottledHandler = throttle(scrollHandler, throttleTime)
      window.addEventListener('scroll', scrollThrottledHandler)
      window.addEventListener('resize', scrollThrottledHandler)
      // Initial check after next tick to ensure element is mounted
      nextTick(() => {
        scrollHandler()
      })
    }
  })

  onUnmounted(() => {
    if (import.meta.client && scrollThrottledHandler) {
      window.removeEventListener('scroll', scrollThrottledHandler)
      window.removeEventListener('resize', scrollThrottledHandler)
    }
  })

  return {
    isInView,
    percentInView,
    percentTop,
    percentCenter,
    scrollPercent,
    scrollValue,
    rect,
  }
}
